import { useState } from 'react';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

// Les sous-champs liste : proposed_value s'AJOUTE à la liste existante
const LIST_SUB_FIELDS = ['examples_valid', 'examples_invalid', 'search_terms'];

function formatCurrentValue(value: string | undefined, isList: boolean): string {
  if (!value) return '—';
  if (!isList) return value;
  // Try to parse as JSON array first
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.join(', ');
    }
  } catch { /* not JSON */ }
  return value;
}

export interface SuggestionItem {
  sub_field: string;            // ex: "notes", "format", "examples_valid", "search_terms"
  rationale: string;
  current_value?: string;       // valeur actuelle (string ou "val1, val2" pour les listes)
  proposed_value?: string;      // nouvelle valeur (remplacement ou ajout selon sub_field)
}

export interface FieldSuggestion {
  field_name: string;
  error_rate: number;
  dominant_error_type: string;
  suggestions: SuggestionItem[];
}

export type SuggestionDecision = 'accepted' | 'rejected' | 'pending';

export interface FieldDecisions {
  [suggestionIndex: number]: {
    decision: SuggestionDecision;
    edited_text?: string;
  };
}

interface Props {
  field: FieldSuggestion;
  decisions: FieldDecisions;
  onDecisionChange: (fieldName: string, suggestionIndex: number, decision: SuggestionDecision, editedText?: string) => void;
}

function getErrorBarColor(rate: number): string {
  if (rate < 0.15) return 'bg-green-500';
  if (rate < 0.40) return 'bg-orange-500';
  return 'bg-red-500';
}

function formatErrorType(type: string): string {
  switch (type) {
    case 'missing': return 'Manquant';
    case 'hallucination': return 'Hallucination';
    case 'format_incorrect': return 'Format incorrect';
    case 'field_confusion': return 'Confusion champ';
    case 'value_mismatch': return 'Valeur incorrecte';
    default: return type;
  }
}

function getSubFieldLabel(subField?: string): string {
  switch (subField) {
    case 'examples_valid': return 'Exemples valides';
    case 'examples_invalid': return 'Exemples invalides';
    case 'search_terms': return 'Termes de recherche';
    case 'notes': return 'Notes / Warnings';
    case 'format': return 'Format attendu';
    case 'description': return 'Description';
    default: return subField || 'Valeur';
  }
}

function getDecisionBorderColor(decision: SuggestionDecision): string {
  switch (decision) {
    case 'accepted': return 'border-green-300 bg-green-50';
    case 'rejected': return 'border-red-300 bg-red-50';
    case 'pending': return 'border-gray-200 bg-white';
  }
}

export function FieldSuggestionCard({ field, decisions, onDecisionChange }: Props) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-900 text-lg">{field.field_name}</h3>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getErrorBarColor(field.error_rate)} text-white`}>
            {(field.error_rate * 100).toFixed(0)}% erreurs
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {formatErrorType(field.dominant_error_type)}
          </span>
        </div>
      </div>

      {/* Suggestions */}
      <div className="p-6 space-y-4">
        {field.suggestions.map((suggestion, idx) => {
          const decision = decisions[idx]?.decision || 'pending';
          const editedText = decisions[idx]?.edited_text;

          return (
            <SuggestionBlock
              key={idx}
              suggestion={suggestion}
              decision={decision}
              editedText={editedText}
              onAccept={() => onDecisionChange(field.field_name, idx, 'accepted', editedText)}
              onReject={() => onDecisionChange(field.field_name, idx, 'rejected')}
              onEditText={(text) => onDecisionChange(field.field_name, idx, decision, text)}
            />
          );
        })}
      </div>
    </div>
  );
}

function SuggestionBlock({
  suggestion,
  decision,
  editedText,
  onAccept,
  onReject,
  onEditText,
}: {
  suggestion: SuggestionItem;
  decision: SuggestionDecision;
  editedText?: string;
  onAccept: () => void;
  onReject: () => void;
  onEditText: (text: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const isList = LIST_SUB_FIELDS.includes(suggestion.sub_field);
  const label = getSubFieldLabel(suggestion.sub_field);

  return (
    <div className={`rounded-lg border p-4 transition-colors ${getDecisionBorderColor(decision)}`}>
      {/* Sub-field badge + decision status */}
      <div className="flex items-start gap-3 mb-3">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 bg-blue-100 text-blue-700">
          {label}
        </span>
        <span className={`inline-flex items-center gap-1 text-xs font-medium flex-shrink-0 ${
          decision === 'accepted' ? 'text-green-600' : decision === 'rejected' ? 'text-red-600' : 'text-gray-400'
        }`}>
          {decision === 'accepted' && <><CheckCircle size={14} /> Acceptée</>}
          {decision === 'rejected' && <><XCircle size={14} /> Refusée</>}
          {decision === 'pending' && <><Clock size={14} /> En attente</>}
        </span>
      </div>

      <p className="text-sm text-gray-600 italic mb-4">{suggestion.rationale}</p>

      {/* Unified layout: current value (left) | proposed value (right) */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase">{label} — Actuel</p>
          <div className="bg-gray-100 rounded p-3 text-sm text-gray-800 font-mono whitespace-pre-wrap min-h-[60px]">
            {formatCurrentValue(suggestion.current_value, isList)}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase">
            {label} — {isList ? 'À ajouter' : 'Proposé'}
          </p>
          {isEditing ? (
            <textarea
              className="w-full border border-blue-300 rounded p-3 text-sm font-mono whitespace-pre-wrap min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-200"
              value={editedText ?? suggestion.proposed_value ?? ''}
              onChange={(e) => onEditText(e.target.value)}
              onBlur={() => setIsEditing(false)}
              autoFocus
            />
          ) : (
            <div
              className="bg-blue-50 rounded p-3 text-sm text-gray-800 font-mono whitespace-pre-wrap min-h-[60px] cursor-pointer hover:ring-2 hover:ring-blue-200"
              onClick={() => setIsEditing(true)}
              title="Cliquer pour modifier"
            >
              {editedText ?? suggestion.proposed_value ?? '—'}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            decision === 'accepted'
              ? 'bg-green-600 text-white'
              : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
          }`}
        >
          <CheckCircle size={14} />
          Accepter
        </button>
        <button
          onClick={onReject}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            decision === 'rejected'
              ? 'bg-red-600 text-white'
              : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
          }`}
        >
          <XCircle size={14} />
          Refuser
        </button>
      </div>
    </div>
  );
}
